/**
 * NavbarCategorySearch — "Search by category" list used in both the desktop
 * Directory dropdown and the mobile drawer. Same grouped-categories layout
 * everywhere, styled per variant.
 */
import { Link } from "react-router-dom";
import { Wrench } from "lucide-react";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { groupCategories } from "@/lib/categoryGroups";

function categorySlug(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

export default function NavbarCategorySearch({ categories, variant, onNavigate }) {
  const { groups, rest } = groupCategories(categories);

  if (variant === "mobile") {
    const renderItem = (cat) => (
      <Link
        key={cat.name}
        to={`/directory?category=${encodeURIComponent(cat.name)}`}
        onClick={onNavigate}
        className="flex items-center justify-between px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
        data-testid={`mobile-nav-category-${categorySlug(cat.name)}`}
      >
        <span className="truncate">{cat.name}</span>
        <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{cat.count}</span>
      </Link>
    );
    return (
      <>
        {groups.map((g) => (
          <div key={g.label}>
            <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-tbc-gold flex items-center gap-1.5">
              <Wrench className="w-3 h-3" />
              {g.label}
            </div>
            {g.items.map(renderItem)}
          </div>
        ))}
        {rest.length > 0 && (
          <>
            {groups.length > 0 && (
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-gray-400">
                All categories
              </div>
            )}
            {rest.map(renderItem)}
          </>
        )}
      </>
    );
  }

  // Desktop dropdown variant
  const renderItem = (cat) => (
    <DropdownMenuItem key={cat.name} asChild>
      <Link
        to={`/directory?category=${encodeURIComponent(cat.name)}`}
        className="w-full cursor-pointer flex items-center justify-between gap-4"
        data-testid={`nav-category-${categorySlug(cat.name)}`}
      >
        <span className="truncate">{cat.name}</span>
        <span className="text-xs text-gray-500 flex-shrink-0">{cat.count}</span>
      </Link>
    </DropdownMenuItem>
  );
  return (
    <>
      {groups.map((g) => (
        <div key={g.label} data-testid={`nav-category-group-${g.testid}`}>
          <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-tbc-gold flex items-center gap-1.5">
            <Wrench className="w-3 h-3" />
            {g.label}
          </div>
          {g.items.map(renderItem)}
          <DropdownMenuSeparator />
        </div>
      ))}
      {rest.length > 0 && (
        <>
          {groups.length > 0 && (
            <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-400">
              All categories
            </div>
          )}
          {rest.map(renderItem)}
        </>
      )}
    </>
  );
}
